import { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../../apiBase';
import { randomUUID } from './randomUUID';
import { apiFetch } from '../../../api/api';
import i18n from '../../../i18n';


/**
 * Custom React hook to extract data from a PDF using an external API.
 *
 * Params match the original code so other components keep working.
 */
export default function usePdfExtraction({
                                             file,
                                             provider,
                                             model,
                                             batchSize,
                                             maxWorkers,
                                             useOcr = false,
                                             useDeepScan = false,
                                             disabled = false,
                                         }) {
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(false);
    const [executionTime, setExecutionTime] = useState(null);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);

    // Cache results for each mode to avoid reprocessing when toggling
    // Key format: `${fileName}_${provider}_${model}_${batchSize}_${maxWorkers}_${useOcr}_${useDeepScan}`
    const cacheRef = useRef({});

    // Dev-only guard for React 18 StrictMode double-invoke on mount:
    // Skip the *first* effect run in development, run the second one.
    const devFirstRunSkippedRef = useRef(false);

    function expandGroupsToItems(highlights) {
        const out = []
        for (const h of highlights) {
            if (h.type !== 'key_value_group') {
                out.push(h)
                continue
            }

            const groupId = randomUUID()

            h.items.forEach((it, idx) => {
                out.push({
                    __uid: `${groupId}_${idx}`,
                    type: 'key_value',
                    groupId,
                    page: h.page,
                    field: { text: it.field?.text ?? it.field, bbox: it.field?.bbox ?? null },
                    value: { text: it.value.text, bbox: it.value.bbox },
                    group_bbox: h.bbox,
                    group_label: h.group_label || null
                })
            })
        }
        return out
    }

    useEffect(() => {
        // Log + guard early
        // console.log('[usePdfExtraction] effect', { file, provider, model, batchSize, maxWorkers, useOcr, disabled });

        if (disabled || !file || !provider || !model) {
            // console.log('[usePdfExtraction] Skipped (disabled, missing file, or no provider/model selected)');
            return;
        }

        // StrictMode: skip the very first run in dev so we don't fire twice
        if (process.env.NODE_ENV !== 'production' && !devFirstRunSkippedRef.current) {
            devFirstRunSkippedRef.current = true;
            // console.log('[usePdfExtraction] Dev StrictMode: skipping first run');
            return;
        }

        // Clear highlights immediately when switching modes to avoid confusion
        setHighlights([]);

        // Generate cache key
        const cacheKey = `${file.name}_${provider}_${model}_${batchSize}_${maxWorkers}_${useOcr}_${useDeepScan}`;

        // Check if we have cached results for this configuration
        if (cacheRef.current[cacheKey]) {
            setHighlights(cacheRef.current[cacheKey].highlights);
            setExecutionTime(cacheRef.current[cacheKey].executionTime);
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        const run = async () => {
            setLoading(true);
            setExecutionTime(null);

            const t0 = (typeof performance !== 'undefined' && performance.now)
                ? performance.now()
                : Date.now();

            try {
                const formData = new FormData();
                formData.append('pdf', file);
                formData.append('provider', provider);
                formData.append('model', model);
                formData.append('batch_size', String(batchSize));
                formData.append('max_workers', String(maxWorkers));
                formData.append('use_ocr', useOcr ? 'true' : 'false');
                formData.append('use_deep_scan', useDeepScan ? 'true' : 'false');
                formData.append('language', (i18n && i18n.language) || 'en');

                // optional: correlate logs across FE/BE
                const requestId = (typeof crypto !== 'undefined' && randomUUID())
                    ? randomUUID()
                    : String(Date.now());
                formData.append('request_id', requestId);

                const res = await apiFetch(`${API_BASE}/extract`, {
                method: 'POST',
                body: formData,
                signal: controller.signal, // <- ensure abort cancels this request
                });

                if (!res.ok) throw new Error(`Extraction failed (${res.status})`);

                const data = await res.json();
                if (!cancelled) {
                    const items = Array.isArray(data) ? data : (data?.highlights ?? [])
                    const expandedHighlights = expandGroupsToItems(items);
                    const t1 = (typeof performance !== 'undefined' && performance.now)
                        ? performance.now()
                        : Date.now();
                    const execTime = ((t1 - t0) / 1000).toFixed(2);

                    // Store in cache
                    cacheRef.current[cacheKey] = {
                        highlights: expandedHighlights,
                        executionTime: execTime
                    };

                    setHighlights(expandedHighlights);
                    setExecutionTime(execTime);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('[usePdfExtraction] extraction error:', err);
                    setExecutionTime(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        run();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [file, provider, model, batchSize, maxWorkers, useOcr, disabled]);

    return {
        highlights,
        loading,
        executionTime,
        selectedAnnotation,
        setSelectedAnnotation,
    };
}
